"use operator overloading";

import { Value } from "./engine";

abstract class Module {
  zeroGrad(): void {
    for (const p of this.parameters()) {
      p.grad = 0;
    }
  }

  abstract parameters(): Value[];
}

export class Neuron extends Module {
  w: Value[];
  b: Value;
  nonlin: boolean;

  constructor(nin: number, nonlin = true) {
    super();
    this.w = Array.from({ length: nin }, () => new Value(Math.random() * 2 - 1));
    this.b = new Value(0);
    this.nonlin = nonlin;
  }

  call(x: Value[]): Value {
    // sum((wi*xi for wi,xi in zip(self.w, x)), self.b)
    const act = this.w.reduce((sum, wi, i) => sum.add(wi.mul(x[i])), this.b);
    return this.nonlin ? act.relu() : act;
  }

  parameters(): Value[] {
    return [...this.w, this.b];
  }

  toString(): string {
    return `${this.nonlin ? "ReLU" : "Linear"}Neuron(${this.w.length})`;
  }
}

export class Layer extends Module {
  neurons: Neuron[];

  constructor(nin: number, nout: number, nonlin = true) {
    super();
    this.neurons = Array.from({ length: nout }, () => new Neuron(nin, nonlin));
  }

  call(x: Value[]): Value | Value[] {
    const out = this.neurons.map(n => n.call(x));
    return out.length === 1 ? out[0] : out;
  }

  parameters(): Value[] {
    return this.neurons.flatMap(n => n.parameters());
  }

  toString(): string {
    const neuronStrs = this.neurons.map(n => n.toString()).join(", ");
    return `Layer of [${neuronStrs}]`;
  }
}

export class MLP extends Module {
  layers: Layer[];

  constructor(nin: number, nouts: number[]) {
    super();
    const sz = [nin, ...nouts];
    this.layers = sz.slice(0, -1).map((_, i) => {
      const nonlin = i !== nouts.length - 1;
      return new Layer(sz[i], sz[i + 1], nonlin);
    });
  }

  call(x: Value[]): Value | Value[] {
    let out: Value | Value[] = x;
    for (const layer of this.layers) {
      out = layer.call(Array.isArray(out) ? out : [out]);
    }
    return out;
  }

  parameters(): Value[] {
    return this.layers.flatMap(layer => layer.parameters());
  }

  toString(): string {
    const layerStrs = this.layers.map(l => l.toString()).join(", ");
    return `MLP of [${layerStrs}]`;
  }
}
